/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  InputNumber,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { render } from '@testing-library/react';
import moment from 'moment';
import { getCatalogosDeModalidadDeSeguridadSocial } from 'api/catalogos/ModalidadDeSeguridadSocial';
import { getCategoriasDeIncapacidades } from 'api/catalogos/categoriasIncapacidades';
import { getSubcategoriasDeIncapacidades } from 'api/catalogos/subcategoriasIncapacidades';
import {
  autorizarIncidenciaAntiguedadParaEnfermedadGeneral,
  deleteIncidenciaAntiguedadParaEnfermedadGeneral,
  getIncidenciaAntiguedadesParaEnfermedadesGeneral,
  getIncidenciaAntiguedadParaEnfermedadGeneral,
  patchIncidenciaAntiguedadParaEnfermedadGeneral,
  postIncidenciaAntiguedadParaEnfermedadGeneral,
} from 'api/nominas/IncidenciaAntiguedadParaEnfermedadGeneral';
import percentFormatter from 'utils/percentFormatter';
import PercentageInput from 'components/PercentageInput';

export const incidenciaAntiguedadParaEnfermedadGeneralPermission = {
  permissionModel: 'incidenciaparametros',
  catalogsModels: [],
  tabsModels: [],
};

const IncidenciaAntiguedadParaEnfermedadGeneral = ({
  permission,
  isEditable = true,
}) => {
  const [form] = Form.useForm();
  const [isAdding, setAdding] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [categoriaIncapacidad, setCategoriaIncapacidad] = useState([]);
  const [subcategoriaIncapacidad, setSubCategoriaIncapacidad] = useState([]);
  const [pageActual, setPageActual] = useState(1);
  const [estados, setEstados] = useState([]);

  const roundNumber = (num) => Math.floor(num * 1e2) / 1e2;

  const formInstances = [
    form,
  ];
  const fetchData = async (page = 1, q = {}) => {
    setLoading(true);
    const _data = await getIncidenciaAntiguedadesParaEnfermedadesGeneral({
      page,
      ...q,
    });
    setData(formatReceived(_data));
    setLoading(false);
  };
  const fetchPaginated = async (page = 1, q = null) => {
    try {
      setLoading(true);
      const cleanPage = Number(String(page).replace(/\D/g, ''));
      const _data = await getIncidenciaAntiguedadesParaEnfermedadesGeneral({
        page: cleanPage,
        ...q,
      });

      const { results } = _data;

      setSelectedRowKeys([]);
      setPageActual(cleanPage);
      setData({ ..._data, results: [...results] });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const searchBy = isEditable ? {} : { estados_globales: ESTATUS_AUTORIZADO };
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getIncidenciaAntiguedadesParaEnfermedadesGeneral(searchBy),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    setAdding(false);
    const [key] = selectedRowKeys;
    const match = responseData || data?.results.find((item) => item.id === key);
    const _antiguedadEnfermedad = await getIncidenciaAntiguedadParaEnfermedadGeneral(match.id);
    setTimeout(() => {
      if (_antiguedadEnfermedad) {
        const formValues = {
          // Formatea la información recibida (según lógica definida en formatReceived)
          ...formatReceived(_antiguedadEnfermedad),

          // Convierte la fecha de vigencia final a objeto moment si existe, si no null
          vigencia_final: _antiguedadEnfermedad.vigencia_final
            ? moment(_antiguedadEnfermedad.vigencia_final)
            : null,
        };
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(formValues));
          setDisableFields(_antiguedadEnfermedad?.estados_globales !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };
  const onClickAdd = () => {
    setAdding(true);
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const valuesForm = form.getFieldsValue();
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len

      const values = {
        ...formatReceived(valuesForm),
        vigencia_final: valuesForm.vigencia_final
          ? valuesForm.vigencia_final.format('YYYY-MM-DD')
          : null,
      };
      const saved = await (key
        ? patchIncidenciaAntiguedadParaEnfermedadGeneral(key, values)
        : postIncidenciaAntiguedadParaEnfermedadGeneral(values)
      );
      if (saved) {
        if (_continue) {
          await fetchData(pageActual);
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData(pageActual);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteIncidenciaAntiguedadParaEnfermedadGeneral(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData(pageActual);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    const dataForForm = {
      ...record,
      vigencia_final: record.vigencia_final
        ? moment(record.vigencia_final)
        : null,
    };
    form.setFieldsValue(dataForForm);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar la Antigüedad Para Enfermedad General ya autorizada');
        return;
      }
      const saved = await autorizarIncidenciaAntiguedadParaEnfermedadGeneral(key);
      if (saved) {
        await fetchData(pageActual);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Antigüedad', // Título superior
      align: 'center',
      children: [
        {
          title: 'Desde',
          dataIndex: 'desde_texto',
          key: 'desde_texto',

          width: 100,
        },
        {
          title: 'Desde Días',
          dataIndex: 'desde_numerico_dias',
          key: 'desde_numerico_dias',

          width: 100,
        },
        {
          title: 'Hasta',
          dataIndex: 'hasta_texto',
          key: 'hasta_texto',

          width: 100,
        },
        {
          title: 'Hasta Días',
          dataIndex: 'hasta_numerico_dias',
          key: 'hasta_numerico_dias',

          width: 100,
        },
      ],
    },
    {
      titleText: 'Total de Días',
      dataIndex: 'total_de_dias',
      key: 'total_de_dias',
      width: 200,
    },
    {
      titleText: '% Sueldo',
      dataIndex: 'porcentaje_de_sueldo',
      key: 'porcentaje_de_sueldo',
      width: 200,
      render: (val) => percentFormatter(val || 0),
    },
    {
      titleText: 'Aplica Días Adicionales',
      dataIndex: 'aplica_dias_adicionales',
      key: 'aplica_dias_adicionales',
      width: 200,
      render: (val) => (val ? 'Si' : 'No'),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    porcentaje_de_pago: [
      requiredRule,
      {
        validator: async (rule, value) => {
          if (typeof value === 'number') {
            if (value < 0) {
              throw new Error('El porcentaje debe ser minimo 0');
            }
            if (value >= 101) {
              throw new Error('El porcentaje debe ser maximo a 100');
            }
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data?.results || []}
          rowSelection={rowSelection}
          pagination={false}
          paginationCallback={fetchPaginated}
          count={data?.count || 0}
          currentPage={pageActual}
          allowExport
          handleOnRowClick={handleOnRowClick}
          controls={isEditable ? {
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          } : {}}
          customActions={isEditable ? [
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'incidenciaparametros', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ] : []}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportParam={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Antigüedad Para Enfermedad General`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="desde_numerico_dias"
                label="Desde Días"
                rules={rules.required}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="desde_texto"
                label="Desde"
                rules={rules.required}
                hasFeedback
              >
                <Input
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="hasta_numerico_dias"
                label="Hasta Días"
                rules={rules.required}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="hasta_texto"
                label="Hasta"
                rules={rules.required}
                hasFeedback
              >
                <Input
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="total_de_dias"
                label="Total de Dias"
                rules={rules.required}
                hasFeedback
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <PercentageInput
                name="porcentaje_de_sueldo"
                label="% Sueldo"
                rules={rules.porcentaje_de_pago}
                disabled={disableFields}
                precision={2}
              />
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="aplica_dias_adicionales"
                label="Aplica Días Adicionales"
                rules={rules.required}
              >
                <Select
                  trueFalse
                  disabled={
                    disableFields
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="vigencia_final"
                label="Vigencia Final"
              >
                <DatePicker
                  allowClear
                  format="YYYY-MM-DD"
                  disabled={isAdding || selectedData?.estados_globales === ESTATUS_INACTIVO}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  activeInactive={selectedData?.estados_globales?.id === ESTATUS_ACTIVO}
                  disabled={selectedRowKeys.length <= 0 || disableFields}
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Antigüedad Para Enfermedad General ${form.getFieldValue('dias_de_incapacidad')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar la Antigüedad Para Enfermedad General ${form.getFieldValue('dias_de_incapacidad')}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar Antigüedad Para Enfermedad General ${form.getFieldValue('dias_de_incapacidad')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default IncidenciaAntiguedadParaEnfermedadGeneral;
